'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import { PLAUSIBLE_EVENTS, trackPlausibleEvent } from '@/lib/client-stats';

interface FloatingNextUpProps {
    nextUpPost: {
        _id: string;
        title: string;
        slug: string;
        language: string;
        learningFocus: string[];
        keywords: string[];
    };
    nativeLanguage: string;
    t: {
        nextUp: string;
        learningFocus: Record<string, string>;
    };
    staticModuleId: string;
}

export default function FloatingNextUp({ nextUpPost, nativeLanguage, t, staticModuleId }: FloatingNextUpProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isStaticVisible, setIsStaticVisible] = useState(false);

    useEffect(() => {
        // Track scroll position
        const handleScroll = () => {
            // Show after scrolling 300px or show by default if document height is less than 700px
            const scrolled = window.scrollY > 300 || document.documentElement?.scrollHeight < 700;
            setHasScrolled(scrolled);
        };

        // Observe the static "Next up" module
        const staticModule = document.getElementById(staticModuleId);
        if (!staticModule) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsStaticVisible(entry.isIntersecting);
                });
            },
            {
                threshold: 0.1, // Trigger when 10% of the element is visible
                rootMargin: '0px 0px -50px 0px', // Add some margin at the bottom
            }
        );

        observer.observe(staticModule);
        window.addEventListener('scroll', handleScroll);

        // Initial check
        handleScroll();

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
        };
    }, [staticModuleId]);

    // Show the floating module only when:
    // 1. User has scrolled enough
    // 2. The static module is not visible
    useEffect(() => {
        setIsVisible(hasScrolled && !isStaticVisible);
    }, [hasScrolled, isStaticVisible]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, y: 100, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 100, scale: 0.8 }}
                    transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 20,
                    }}
                    className="fixed bottom-4 right-4 z-50 max-w-[calc(100vw-2rem)] sm:max-w-sm"
                >
                    <Link
                        href={`/learn/${nextUpPost.language}/${nativeLanguage}/${nextUpPost.slug}`}
                        className="block"
                        onClick={() => trackPlausibleEvent({name: PLAUSIBLE_EVENTS.seoNextArticlePopupTap})}
                    >
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-primary/20 overflow-hidden backdrop-blur-sm"
                        >
                            {/* Header with gradient */}
                            <div className="bg-gradient-to-br from-emerald-600 to-green-700 px-2 py-2 flex items-center gap-2">
                                <ArrowRightCircleIcon className="w-5 h-5 text-white" />
                                <span className="text-white text-sm font-semibold">
                                    {t.nextUp}
                                </span>
                            </div>

                            {/* Content */}
                            <div className="p-2">
                                <h3
                                    className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 mt-0 mb-2 line-clamp-2"
                                    dangerouslySetInnerHTML={{ __html: nextUpPost.title }}
                                />

                                {/* Keywords */}
                                <div className="flex flex-wrap gap-x-2 gap-y-1.5 mb-2.5">
                                    {nextUpPost.keywords.map((keyword, ix) => (
                                        <span
                                            key={ix}
                                            className="text-blue-700 text-xs font-medium rounded-full"
                                        >
                                            {keyword}
                                        </span>
                                    ))}
                                </div>

                                {/* Learning focus pills */}
                                <div className="flex flex-wrap gap-1.5">
                                    {nextUpPost.learningFocus.filter((focus) => t.learningFocus[focus]).slice(0, 2).map((focus, ix) => (
                                        <span
                                            key={ix}
                                            className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full"
                                        >
                                            {t.learningFocus[focus]}
                                        </span>
                                    ))}
                                    {nextUpPost.learningFocus.length > 2 && (
                                        <span className="text-xs text-gray-500 px-2 py-0.5">
                                            +{nextUpPost.learningFocus.length - 2}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Subtle hover indicator */}
                            <div className="h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                        </motion.div>
                    </Link>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
